import { roleDef } from '../roles/definitions';
import type { RoleId, RoleSetup } from '../types';
import { ROLE_IDS } from '../types';

export const MIN_PLAYERS = 6;
export const MAX_PLAYERS = 12;

export interface SetupIssue {
  code: string;
  message: string;
}

export function countRoles(roles: readonly RoleId[]): Partial<Record<RoleId, number>> {
  const counts: Partial<Record<RoleId, number>> = {};
  for (const r of roles) counts[r] = (counts[r] ?? 0) + 1;
  return counts;
}

export function slotOccupancy(setup: RoleSetup | null, playerCount: number): { filled: number; total: number } {
  return { filled: setup?.roles.length ?? 0, total: playerCount };
}

/** Prüft eine Rollenkomposition vollständig. Leere Liste = gültig. */
export function validateSetup(setup: RoleSetup, playerCount: number): SetupIssue[] {
  const issues: SetupIssue[] = [];

  if (playerCount < MIN_PLAYERS || playerCount > MAX_PLAYERS) {
    issues.push({
      code: 'PLAYER_COUNT',
      message: `Es werden ${MIN_PLAYERS} bis ${MAX_PLAYERS} Spieler benötigt (aktuell ${playerCount}).`,
    });
  }

  for (const role of setup.roles) {
    if (!(ROLE_IDS as readonly string[]).includes(role)) {
      issues.push({ code: 'UNKNOWN_ROLE', message: `Unbekannte Rolle: ${String(role)}.` });
    }
  }

  if (setup.roles.length !== playerCount) {
    issues.push({
      code: 'SLOT_COUNT',
      message: `${setup.roles.length} / ${playerCount} Rollenplätze belegt.`,
    });
  }

  const counts = countRoles(setup.roles);
  for (const [role, n] of Object.entries(counts) as [RoleId, number][]) {
    const def = roleDef(role);
    if (def && n > def.maxCount) {
      issues.push({
        code: 'ROLE_LIMIT',
        message: `${def.name} darf höchstens ${def.maxCount}× vorkommen (aktuell ${n}×).`,
      });
    }
  }

  if ((counts.VAMPIRE ?? 0) < 1) {
    issues.push({ code: 'NO_VAMPIRE', message: 'Die Komposition benötigt mindestens einen Vampir.' });
  }

  if (setup.drunk.enabled) {
    const covered = setup.drunk.coveredRole;
    if (covered === 'RANDOM') {
      if (!setup.roles.some((r) => roleDef(r)?.drunkEligible)) {
        issues.push({
          code: 'DRUNK_NO_ELIGIBLE',
          message: 'Keine Rolle der Komposition kann vom Trunkenbold verdeckt werden.',
        });
      }
    } else {
      if (!roleDef(covered)?.drunkEligible) {
        issues.push({
          code: 'DRUNK_NOT_ELIGIBLE',
          message: `${roleDef(covered)?.name ?? covered} kann nicht vom Trunkenbold verdeckt werden.`,
        });
      }
      if (!setup.roles.includes(covered)) {
        issues.push({
          code: 'DRUNK_ROLE_MISSING',
          message:
            'Der Trunkenbold verdeckt eine vorhandene Rollenposition – die gewählte Rolle muss in der Komposition enthalten sein.',
        });
      }
    }
  }

  // Geliebte: Es muss mindestens ein erlaubtes Paar existieren (nicht Vampir + Vampir).
  const nonVampires = setup.roles.filter((r) => r !== 'VAMPIRE').length;
  if (setup.roles.length >= 2 && nonVampires < 1) {
    issues.push({ code: 'NO_LOVER_PAIR', message: 'Es lässt sich kein erlaubtes Geliebtenpaar bilden.' });
  }

  return issues;
}
